/**
 * Calendar dates in a chosen time zone.
 *
 * ── A `Date` is an instant, not a day ───────────────────────────────────────
 *
 * "Today", "the last seven days" and the label under a bar are questions about a
 * calendar, and which calendar depends on who is asking. For a viewer in
 * Pacific/Midway (−11) or Asia/Kathmandu (+05:45) the browser's idea of today is
 * the wrong day for most of the twenty-four hours - and in this product the
 * question is sharper than usual, because an assessment is filed at a **site**
 * and a site carries its own `timezone`. A supervisor in Hyderabad reviewing a
 * yard in Texas has two candidate days for every record and needs the one the
 * crew was standing in.
 *
 * A `CalDate` is a plain year/month/day. No instant, no zone, nothing that can
 * drift across a DST boundary, and nothing that can be accidentally formatted in
 * the browser's zone.
 *
 * ── The trap this exists to avoid ───────────────────────────────────────────
 *
 * `new Date("2026-07-10")` parses as **UTC midnight**, which in any negative
 * offset formats as the 9th. Any code that round-trips a "YYYY-MM-DD" through
 * `new Date(...)` and back is off by one for roughly half the planet, silently,
 * and only for people in the wrong hemisphere of the null island. Parsing the
 * parts by hand is the whole fix, and it is why `parseValue` returns a `CalDate`
 * rather than a `Date`.
 */
export interface CalDate {
  y: number;
  m: number;
  d: number;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" - what a date filter and a `<input type="date">` both speak. */
export function toValue(date: Date | CalDate): string {
  if (date instanceof Date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
  return `${date.y}-${pad(date.m)}-${pad(date.d)}`;
}

export function parseValue(value: string): CalDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  return match ? { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) } : null;
}

/**
 * A `CalDate` as a JS `Date` at **local** midnight - the shape `react-day-picker`
 * renders and compares. Local rather than UTC so the browser's own formatting of
 * it agrees with the day it names.
 */
export function toLocalDate(date: CalDate): Date {
  return new Date(date.y, date.m - 1, date.d);
}

/** The calendar date it is right now in `tz`, or in the browser's zone. */
export function todayIn(tz?: string | null): CalDate {
  try {
    // en-CA formats as YYYY-MM-DD, which is the one thing every locale argument
    // in this file is trying to avoid having to think about.
    const formatted = new Intl.DateTimeFormat("en-CA", {
      ...(tz ? { timeZone: tz } : {}),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());

    const [y, m, d] = formatted.split("-").map(Number);
    return { y, m, d };
  } catch {
    // An unknown IANA zone throws rather than falling back, and a picker that
    // cannot render is worse than one showing the browser's today.
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
  }
}

/**
 * Shift a calendar date by whole days.
 *
 * Done in UTC deliberately: it is calendar arithmetic, so a DST boundary must
 * not turn "seven days ago" into six days and twenty-three hours. The UTC
 * instant here is scaffolding and never escapes the function.
 */
export function addDays(date: CalDate, days: number): CalDate {
  const shifted = new Date(Date.UTC(date.y, date.m - 1, date.d));
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return {
    y: shifted.getUTCFullYear(),
    m: shifted.getUTCMonth() + 1,
    d: shifted.getUTCDate(),
  };
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "12 Aug". The compact form, for a trigger that has to fit two of them. */
export function formatDay(date: Date): string {
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(date);
}

/** "12 Aug 2026". For a single date, where the year is worth the width. */
export function formatFullDay(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

/* ─────────────────────────── Times, in a named zone ─────────────────────── */

/**
 * ── Why the schedule breaks the "client owns display conversion" rule ───────
 *
 * `docs/01-conventions.md` says the API is timezone-agnostic and the client
 * renders in the viewer's zone. That is right for a timestamp - "this was
 * submitted at 14:02" means the reader's 14:02.
 *
 * A calendar is the exception, and it is not a small one. A supervisor in
 * Hyderabad scheduling nine o'clock at a Texas yard means nine o'clock **in
 * Texas**. Render that instant in the browser's zone and it appears at 20:30 the
 * previous evening, on the wrong day, in the wrong week. So everything below
 * takes an explicit `tz` - the **site's** `timezone` column - and the console
 * names the zone on screen so nobody has to guess which clock they are reading.
 *
 * See docs/decisions.md #25. Storage never changes: `timestamptz`, UTC, and the
 * wire format is an ISO instant.
 */

/** Wall-clock parts of an instant, as read in `tz`. */
export interface ZonedParts extends CalDate {
  hh: number;
  mm: number;
}

/**
 * `Intl` is the only thing in the browser that carries the IANA database, and
 * `formatToParts` is the only way to get numbers back out of it rather than a
 * localised string that then has to be re-parsed.
 */
function partsIn(instant: Date, tz: string): ZonedParts {
  const fields = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    // Without this, midnight formats as "24" in en-US and the day is off by one
    // for exactly one minute a day - the kind of bug that reproduces at 00:00
    // and nowhere else.
    hourCycle: "h23",
  }).formatToParts(instant);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(fields.find((part) => part.type === type)?.value ?? "0");

  return { y: get("year"), m: get("month"), d: get("day"), hh: get("hour"), mm: get("minute") };
}

/** The wall clock in `tz` at the moment `iso` names. */
export function zonedParts(iso: string, tz: string): ZonedParts {
  return partsIn(new Date(iso), tz);
}

/**
 * `tz`'s offset from UTC, in minutes, **at a given instant**.
 *
 * At an instant, not for a zone: America/Chicago is -300 in July and -360 in
 * January, and a scheduler that assumes one of them is wrong for half the year.
 */
function offsetMinutesAt(instant: Date, tz: string): number {
  const p = partsIn(instant, tz);
  const asIfUtc = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm);
  // Round to the minute: formatToParts drops seconds, so the difference carries
  // the instant's own seconds as noise.
  return Math.round((asIfUtc - instant.getTime()) / 60000);
}

/**
 * A wall-clock time in `tz` → the UTC instant it names.
 *
 * The inverse of `zonedParts`, and the only genuinely fiddly function here,
 * because the answer depends on the offset and the offset depends on the answer.
 *
 * Two passes resolve it. Guess by pretending the wall clock is UTC, measure the
 * zone's offset at that guess, correct; then measure again at the corrected
 * instant and correct once more. The second pass only changes anything within an
 * hour or two of a DST transition - which is exactly where a one-pass version is
 * silently an hour out, and exactly the morning somebody would notice.
 *
 * ── The two hours a year that have no single right answer ──────────────────
 *
 * Spring forward and 02:30 does not exist; fall back and 01:30 happens twice.
 * There is no correct instant to return, only a defensible one: this lands on
 * the first, which for the skipped hour means the clock reads an hour later than
 * asked, and for the repeated hour means the earlier of the two. Both are what a
 * person means when they schedule at that time. Do not "fix" this without a
 * failing test that says what the right answer would be instead.
 */
export function instantFrom(date: CalDate, hh: number, mm: number, tz: string): string {
  const wallAsUtc = Date.UTC(date.y, date.m - 1, date.d, hh, mm);

  let guess = new Date(wallAsUtc - offsetMinutesAt(new Date(wallAsUtc), tz) * 60000);
  guess = new Date(wallAsUtc - offsetMinutesAt(guess, tz) * 60000);

  return guess.toISOString();
}

/** The calendar day an instant falls on in `tz`. The key a calendar buckets by. */
export function dayKeyIn(iso: string, tz: string): string {
  const p = zonedParts(iso, tz);
  return toValue(p);
}

/**
 * Minutes from local midnight, which is what positions an event in a day column.
 *
 * Clamped to the day, so an appointment that started yesterday and is still
 * running renders from the top of today's column rather than at a negative
 * offset that the browser resolves by drawing it off-screen.
 */
export function minutesIntoDayIn(iso: string, tz: string, dayKey?: string): number {
  const p = zonedParts(iso, tz);
  const minutes = p.hh * 60 + p.mm;
  if (!dayKey) return minutes;

  const own = toValue(p);
  if (own < dayKey) return 0;
  if (own > dayKey) return 24 * 60;
  return minutes;
}

/**
 * The UTC instants bounding `days` calendar days starting at `from`, in `tz`.
 *
 * What the calendar sends as `?from=&to=`. Computed from local midnight to local
 * midnight rather than by adding 24-hour blocks, because a week containing a DST
 * transition is 167 or 169 hours long and the missing hour is otherwise an
 * appointment that vanishes from the last day of the view.
 */
export function zonedRange(from: CalDate, tz: string, days: number): { from: string; to: string } {
  return {
    from: instantFrom(from, 0, 0, tz),
    to: instantFrom(addDays(from, days), 0, 0, tz),
  };
}

/** "09:30" in `tz`. Locale-aware, so a US viewer gets "9:30 AM". */
export function formatTimeIn(iso: string, tz: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

/**
 * The wall clock in `tz`, split so a range can decide what to leave out.
 *
 * `formatToParts` rather than a formatted string, because the two things worth
 * dropping - a `:00` and a repeated meridiem - are only droppable if you can see
 * them separately, and re-parsing "9:00 AM" back apart is how a formatter ends
 * up locale-specific by accident.
 */
function clockPartsIn(iso: string, tz: string): { time: string; period: string | null } {
  const parts = new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
  }).formatToParts(new Date(iso));

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";

  const minute = value("minute");
  // A `dayPeriod` part is what says the locale is on a 12-hour clock. Its
  // absence is the test below, and it is a more honest one than a list of
  // locales that are: the same locale can be either, depending on the reader's
  // own settings.
  const period = parts.find((part) => part.type === "dayPeriod")?.value ?? null;

  // ":00" comes off only on a 12-hour clock. In a 24-hour locale "09:00" is the
  // convention and a bare "9" is not a time anybody writes.
  return { time: period && minute === "00" ? value("hour") : `${value("hour")}:${minute}`, period };
}

/**
 * "9 - 10 am", "9:15 - 10 am", "11 am - 12:30 pm". A range, as short as it can
 * be without becoming ambiguous.
 *
 * A calendar chip is a few centimetres wide and prints two times on it, so
 * "9:00 AM - 10:00 AM" spends most of a line on four characters that are always
 * the same and one meridiem that is usually repeated. Both come off:
 *
 * - `:00` is dropped, so a job on the hour reads "9" rather than "9:00".
 * - The meridiem is carried **once** when both ends share it. It comes straight
 *   back when they differ, because "11 - 12:30 pm" would otherwise read as a
 *   ninety-minute job starting at 11am rather than the ninety minutes it is.
 *
 * Neither shortening is applied on a 24-hour clock, where "09:00" is the
 * convention and there is no meridiem to deduplicate - so this returns the plain
 * range for those readers rather than inventing a format for them.
 *
 * Lower case on purpose: "am" is quieter than "AM" beside a title that is the
 * thing being read, and this string is never the loudest text on a chip.
 */
export function formatTimeRangeIn(startIso: string, endIso: string, tz: string): string {
  const start = clockPartsIn(startIso, tz);
  const end = clockPartsIn(endIso, tz);

  if (!start.period || !end.period) return `${start.time} - ${end.time}`;

  const head = start.period === end.period ? start.time : `${start.time} ${lower(start.period)}`;
  return `${head} - ${end.time} ${lower(end.period)}`;
}

/** "9 am", "9:15 am". One end of the above, for a month cell with room for one. */
export function formatTimeShortIn(iso: string, tz: string): string {
  const { time, period } = clockPartsIn(iso, tz);
  return period ? `${time} ${lower(period)}` : time;
}

/**
 * `toLocaleLowerCase` rather than `toLowerCase`: Turkish maps a dotted capital I
 * to a dotted lower-case one, and the ASCII version silently does not.
 */
function lower(value: string): string {
  return value.toLocaleLowerCase();
}

/**
 * "Wed 20 Aug" in `tz`. The day on its own, for a card that already prints a
 * time on the line beside it.
 *
 * No year: everything this renders is within a few weeks of now, and a year on
 * every row is a column of "2026" that never changes.
 */
export function formatDayIn(iso: string, tz: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(iso));
}

/**
 * "28 Aug" in `tz`. For a chart's x-axis, where `formatDayIn`'s weekday is the
 * third of the label's width and the axis is the one place there is none to
 * spare - a fortnight of "Fri 28 Aug" overprints itself on a phone.
 */
export function formatDayAxisIn(iso: string, tz: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    day: "numeric",
    month: "short",
  }).format(new Date(iso));
}

/** "12 Aug, 09:30" in `tz`. For a list row, where the day is not implied. */
export function formatDayTimeIn(iso: string, tz: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

/**
 * "GMT+05:30" - the zone as an offset, shown beside every schedule so the
 * reader knows which clock they are looking at.
 *
 * Not an abbreviation ("IST", "CDT"): most zones have none, `Intl` echoes the
 * offset back for them anyway, and for the zones that do have one, the name
 * comes from the browser's own ICU data - which does not agree with mobile's
 * `timezone` package. An offset is arithmetic rather than a name lookup, so it
 * is defined for every zone and matches `zoneOffsetLabel` in
 * `mobile-app/lib/core/util/dates.dart` exactly.
 */
export function zoneOffset(tz: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(at);
    const raw = parts.find((part) => part.type === "timeZoneName")?.value ?? "GMT+00:00";
    const match = /^GMT([+-]?)(\d{1,2})(?::(\d{2}))?$/.exec(raw);
    if (!match) return raw;
    const [, sign, hh, mm] = match;
    return `GMT${sign || "+"}${hh.padStart(2, "0")}:${mm ?? "00"}`;
  } catch {
    // An unknown IANA zone throws rather than falling back, and a schedule that
    // cannot render is worse than one labelled with a raw zone name.
    return tz;
  }
}

/** "2h 30m", "45m". A span already in minutes, for an average or a delta. */
function formatMinutes(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  if (hours === 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
}

/** "2h 30m", "45m". Duration between two instants, for a form and a list cell. */
export function formatDuration(startIso: string, endIso: string): string {
  return formatMinutes((Date.parse(endIso) - Date.parse(startIso)) / 60000);
}

/**
 * "20 minutes ago", "in 2 hours". Coarse on purpose.
 *
 * In the **reader's own** clock, unlike everything above it that takes a `tz`.
 * That is the ordinary rule in this product (`docs/01-conventions.md`) and the
 * calendar is the documented exception: "this happened twenty minutes ago" is a
 * fact about the person reading it, not about the yard it happened at.
 *
 * `Intl.RelativeTimeFormat` rather than a hand-rolled ladder, because the
 * pluralisation and the "in"/"ago" wrapping are both locale rules and both get
 * written wrong by hand.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const seconds = (Date.parse(iso) - now.getTime()) / 1000;
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size || unit === "minute") {
      return format.format(Math.round(seconds / size), unit);
    }
  }

  return format.format(0, "minute");
}

/** Largest first, so the loop lands on the coarsest unit that still reads as a
 *  number rather than as "43200 seconds". */
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
];

/** "09:30" from an instant, for an `<input type="time">` value. Always h23. */
export function timeInputValue(iso: string, tz: string): string {
  const p = zonedParts(iso, tz);
  return `${pad(p.hh)}:${pad(p.mm)}`;
}

/** The inverse: "09:30" → `{ hh: 9, mm: 30 }`, or null if it is not a time. */
export function parseTimeInput(value: string): { hh: number; mm: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value || "");
  if (!match) return null;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (hh > 23 || mm > 59) return null;
  return { hh, mm };
}
